/* ============================================================================
   KEEPING A BUILD AND ITS UPGRADES TOGETHER IN THE CART

   A configured bike goes into the cart as several lines: the bike itself, plus
   one line per paid upgrade from the hidden Builder Add-ons product. Nothing
   tied them together - every upgrade line carried the same property, so with
   two configured bikes in the cart their upgrades were indistinguishable, and
   removing one bike left its upgrades behind, billing for parts with no bike.

   HOW THEY ARE TIED
   Every line added in one click gets the same random id under a '_build'
   property. The leading underscore is Shopify's convention for a private
   property: it survives into the order and is visible in admin, but is hidden
   from the customer's cart and checkout.

   Because the id is unique per click, two identical builds never merge into
   one line of quantity 2 - they stay two separate builds, which is what lets
   the second one keep its own upgrades.

   HOW ORPHANS ARE CLEARED
   Rather than trying to detect a removal as it happens - which means guessing
   at the theme's buttons, the drawer, the cart page and the quantity stepper -
   this enforces an invariant after any cart change: a group whose bike is gone
   has its upgrade lines removed.

   WHY QUANTITY IS PINNED TO 1
   The upgrades are one-per-bike. If a shopper set the bike to quantity 2 the
   upgrade lines would stay at 1 and they would be charged for two bikes with
   one set of parts. Any build line above 1 is put back to 1.

   LOAD ORDER MATTERS
   This wraps fetch, and the builder section wraps fetch too - to append the
   upgrade lines. The section's wrapper is installed later, so it runs FIRST
   and this one sees the finished payload with the upgrades already in it.
   Moving this script after the section would send the upgrades in unstamped.
   ============================================================================ */
(function () {

  if (window.__pmCartBuilds) return;
  window.__pmCartBuilds = true;

  var ADDON_HANDLE = 'builder-add-ons';
  var KEY = '_build';
  var MATCH_KEY = 'Match my bike';

  /* Only long enough to collapse a burst of events from one user action into
     a single pass. */
  var SETTLE_MS = 60;

  var busy = false;
  var timer = null;
  var skipNext = false;

  function newId() {
    return 'b' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
  }

  /* The bike is the line that is NOT an upgrade. Upgrades are identifiable in
     the payload because the section gives each one a 'For build' property,
     while the bike carries the configuration itself. Keying off that rather
     than position means a future change to the order items are appended in
     cannot quietly put the customer's note on a $1 seat cover. */
  function isUpgrade(item) {
    return !!(item && item.properties && item.properties['For build']);
  }

  /* ---- 1. Stamp every line added in one click, and ask for the markup -----
     `sections` is the reason the cart no longer lags: Shopify renders those
     sections as part of this request and returns the HTML with the response,
     so cart-refresh.js has everything it needs the moment the add completes
     and makes no further round trips. */
  var passThrough = window.fetch;
  window.fetch = function (input, init) {
    try {
      var url = (typeof input === 'string') ? input : (input && input.url) || '';
      if (url.indexOf('/cart/add.js') > -1 && init && typeof init.body === 'string') {
        var payload = JSON.parse(init.body);
        if (payload && payload.items && payload.items.length) {
          var bid = newId();

          /* Read once per add, not per line. */
          var note = '';
          try {
            if (window.__pmMatch && window.__pmMatch.note) note = window.__pmMatch.note();
          } catch (e) { /* the note must never block the add */ }

          var noteDone = false;

          for (var i = 0; i < payload.items.length; i++) {
            var item = payload.items[i];
            item.properties = item.properties || {};
            item.properties[KEY] = bid;
            item.quantity = 1;

            /* Visible, no underscore: the customer should see what they asked
               for in their own cart and confirmation, and so should whoever
               picks the build up in admin. Bike only. */
            if (note && !noteDone && !isUpgrade(item)) {
              item.properties[MATCH_KEY] = note;
              noteDone = true;
            }
          }

          if (!payload.sections && window.__pmCart && window.__pmCart.sectionIds) {
            var ids = window.__pmCart.sectionIds();
            if (ids.length) payload.sections = ids.join(',');
          }

          init.body = JSON.stringify(payload);

          /* A fresh add is consistent by construction, so the reconcile that
             the resulting cart:update would trigger is pure latency. */
          skipNext = true;
        }
      }
    } catch (e) { /* a failed stamp must never block the add */ }
    return passThrough.apply(this, arguments);
  };

  /* ---- 2. Reconcile ------------------------------------------------------ */
  function getCart() {
    return passThrough('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  /* One request for every correction, not one per line. /cart/update.js takes
     a map of line key to quantity, and returns the new cart - which is handed
     straight to the refresh so it does not have to fetch it again. */
  function applyUpdates(updates) {
    return passThrough('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ updates: updates })
    }).then(function (r) { return r.json(); });
  }

  function reconcile() {
    if (busy) return Promise.resolve(0);
    busy = true;

    return getCart().then(function (cart) {
      var groups = {};
      var items = (cart && cart.items) || [];

      for (var i = 0; i < items.length; i++) {
        var build = items[i].properties && items[i].properties[KEY];
        if (!build) continue;
        if (!groups[build]) groups[build] = [];
        groups[build].push(items[i]);
      }

      var updates = {};
      var count = 0;

      for (var key in groups) {
        if (!Object.prototype.hasOwnProperty.call(groups, key)) continue;
        var group = groups[key];

        var hasBike = false;
        for (var j = 0; j < group.length; j++) {
          if (group[j].handle !== ADDON_HANDLE) { hasBike = true; break; }
        }

        for (var k = 0; k < group.length; k++) {
          if (!hasBike) {
            updates[group[k].key] = 0;   /* bike gone - these belong to nothing */
            count++;
          } else if (group[k].quantity > 1) {
            updates[group[k].key] = 1;   /* one set of parts per bike */
            count++;
          }
        }
      }

      if (!count) return null;
      return applyUpdates(updates);
    }).then(function (newCart) {
      busy = false;
      if (newCart && window.__pmCart && window.__pmCart.refresh) {
        window.__pmCart.refresh(newCart);
        return 1;
      }
      return 0;
    }).catch(function () {
      busy = false;
      return 0;
    });
  }

  function schedule() {
    if (skipNext) { skipNext = false; return; }
    window.clearTimeout(timer);
    timer = window.setTimeout(reconcile, SETTLE_MS);
  }

  document.addEventListener('cart:update', schedule);

  /* Catch routes that do not announce themselves. */
  window.fetch = (function (inner) {
    return function (input, init) {
      var out = inner.apply(this, arguments);
      try {
        var url = (typeof input === 'string') ? input : (input && input.url) || '';
        if (url.indexOf('/cart/change') > -1 || url.indexOf('/cart/update') > -1) {
          if (!busy) out.then(function () { schedule(); }).catch(function () {});
        }
      } catch (e) {}
      return out;
    };
  })(window.fetch);

  /* And on arrival, in case the cart was left inconsistent in another tab. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { reconcile(); });
  } else {
    reconcile();
  }

  window.__pmBuilds = { reconcile: reconcile };
})();